<template>
  <div
    v-if="eventData"
    class="w-full lg:w-[420px] flex flex-col gap-4 mt-8 kft-product__checkout"
  >
    <!-- Buy box + giftcard card. This is the sticky-bar trigger target -->
    <!-- (team-event / corporate-gifting cards below are intentionally excluded). -->
    <div ref="buyBoxContainer" class="flex flex-col gap-4">
      <!-- Placeholder to prevent CLS -->
      <div
        v-show="!isBuyBoxReady"
        class="preloader loading-effect"
        :class="isSpecialGiftcardEvent ? 'min-h-[190px]' : 'min-h-[300px]'"
      ></div>

      <EventBuyBoxNoDates
        v-if="hasNoDates"
        :show-giftcard="!!shouldEnableGiftcard"
        :show-date-request="!isKitsPage"
        :show-waitlist="!isKitsPage"
        :is-embedded="isEmbedded"
        :background-color="backgroundColor"
        :text-color="textColor"
        @add-item-to-cart="emit('add-item-to-cart')"
      />

      <!-- EventBuyBox with v-if to only render when dates are available -->
      <EventBuyBox
        v-if="isBuyBoxShown"
        :is-embedded="isEmbedded"
        :text-color="textColor"
        :background-color="backgroundColor"
        :card-background-color="cardBackgroundColor"
        :is-kits-page="isKitsPage"
        :enable-giftcard="false"
        :available-dates-quantity="availableDatesQuantity"
        :first-available-date="firstAvailableDate"
        :first-available-month="firstAvailableMonth"
        :is-multi-date="isMultiDateFromCalendar"
        @dates-loaded="emit('dates-loaded')"
        @pricings-loaded="emit('pricings-loaded')"
        @add-item-to-cart="emit('add-item-to-cart')"
        @toggle-date-picker="emit('toggle-date-picker')"
      />

      <LazyEventGiftcardBuyBoxCard
        v-if="isGiftcardCardShown"
        :text-color="textColor"
        :enable-embedded="isEmbedded"
        :background-color="backgroundColor"
        :card-background-color="cardBackgroundColor"
        display-price
        :type="eventGetters.getIsTeamEvent(eventData) ? 'PRIVATE' : 'PUBLIC'"
        :should-display-buy-box="shouldDisplayEventBuybox"
        :is-special-giftcard-event="isSpecialGiftcardEvent"
        @add-item-to-cart="emit('add-item-to-cart')"
      />
    </div>

    <LazyTeameventRequestCard
      v-if="shouldDisplayTeamEventRequestCard"
      :price="teamEventRequestPrice"
    />

    <LazyCorporateGiftingCard
      v-if="shouldDisplayCorporateGiftingCard"
      :event-type="isSpecialGiftcardEvent ? 'GIFT_CARD' : 'BOX'"
    />
  </div>
</template>

<script lang="ts" setup>
import EventBuyBox from './buybox/EventBuyBox.vue'
import type {
  EventCalendarDayMap,
  EventDescription,
} from '~/composables/types/event'
import { useIsSubdomain } from '~/composables/useSubdomain'
import { eventDateGetters } from '~/utils/eventDateGetters'

const { locale } = useI18n()

const eventBuyBoxVisibleElements = {
  giftcard: true,
  ...(inject('eventBuyBoxVisibleElements') as {
    calendar?: boolean
    requestButtons?: boolean
    giftcard?: boolean
  }),
}

const {
  textColor,
  backgroundColor,
  cardBackgroundColor = 'transparent',
  isEmbedded = false,
} = defineProps<{
  textColor?: string
  backgroundColor?: string
  cardBackgroundColor?: string
  isEmbedded?: boolean
}>()

const emit = defineEmits<{
  (
    e:
      | 'dates-loaded'
      | 'pricings-loaded'
      | 'toggle-date-picker'
      | 'add-item-to-cart'
  ): void
}>()

const eventData = inject<EventDescription | undefined>('eventData')
const isSpecialGiftcardEvent = inject('isSpecialGiftcardEvent') as boolean

// Guard against missing eventData - component will not render without it
if (!eventData && import.meta.dev) {
  console.warn('EventDetailsSidebar: eventData not provided via inject')
}
const router = useRouter()
const isKitsPage = computed(
  () =>
    router.currentRoute.value.name?.toString().split('___')[0] ===
      'k-boxPermalink' ||
    (isEmbedded && eventData && eventGetters.isBoxEvent(eventData))
)

const isSubdomain = useIsSubdomain()
const calendarDates = ref<EventCalendarDayMap | Record<any, any> | undefined>(
  undefined
)

const isBuyBoxReady = ref(isSpecialGiftcardEvent)

const firstAvailableDate = computed(() => {
  if (!calendarDates.value) return undefined
  return eventDateGetters.getFirstAvailableDate(calendarDates.value)
})

const firstAvailableMonth = computed(() => {
  if (!calendarDates.value) return undefined
  return calendarDates.value
})

const areThereDatesAvailable = computed(() =>
  eventDateGetters.areThereDatesAvailable(calendarDates.value)
)

const availableDatesQuantity = computed(
  () => Object.keys(calendarDates.value ?? {}).length || 0
)

const isMultiDateFromCalendar = computed(() => {
  const firstDay = Object.values(calendarDates.value ?? {})[0]
  return eventDateGetters.isMultiDate(firstDay?.dates?.[0])
})

const sessionCountFromCalendar = computed(() => {
  const firstDay = Object.values(calendarDates.value ?? {})[0]
  return eventDateGetters.getSessionsCount(firstDay?.dates?.[0])
})

provide('sessionCountOverride', sessionCountFromCalendar)

const shouldEnableGiftcard = computed(
  () =>
    eventData &&
    eventBuyBoxVisibleElements.giftcard &&
    !eventGetters.getIsTeamEvent(eventData) &&
    (!isSubdomain.value ||
      supplierGetters.getHasGiftcardEmbedded(
        eventGetters.getSupplier(eventData)
      ))
)

const shouldDisplayEventBuybox = computed(() => {
  return !isSpecialGiftcardEvent && areThereDatesAvailable.value
})

const hasNoDates = computed(
  () =>
    isBuyBoxReady.value &&
    !isSpecialGiftcardEvent &&
    !areThereDatesAvailable.value
)

const isKitsAvailable = computed(() => {
  return (
    isKitsPage.value &&
    areThereDatesAvailable.value &&
    calendarDates.value?.[0]?.tickets_left > 0
  )
})

// The three cards that can appear in the buy box area. Each computed matches
// the matching component's v-if in the template so they stay in sync.
const isBuyBoxShown = computed(
  () =>
    isBuyBoxReady.value &&
    (shouldDisplayEventBuybox.value || isKitsAvailable.value)
)
const isGiftcardCardShown = computed(
  () => !!shouldEnableGiftcard.value && !hasNoDates.value
)

// Sticky-bar trigger target: the buy box / giftcard area only. Exposed to the
// parent so it can observe visibility and scroll here (see EventDetails.vue).
const buyBoxContainer = useTemplateRef<HTMLElement>('buyBoxContainer')

// The sticky bar has something to point at when any of those cards is shown.
const hasStickyBuyBoxTarget = computed(
  () => hasNoDates.value || isBuyBoxShown.value || isGiftcardCardShown.value
)

defineExpose({ buyBoxContainer, hasStickyBuyBoxTarget })

const shouldDisplayTeamEventRequestCard = computed(
  () =>
    eventData &&
    !isEmbedded &&
    !isSpecialGiftcardEvent &&
    !hasNoDates.value &&
    !eventGetters.isBoxEvent(eventData)
)

const shouldDisplayCorporateGiftingCard = computed(
  () =>
    eventData &&
    !isEmbedded &&
    !isSubdomain.value &&
    (isSpecialGiftcardEvent || eventGetters.isBoxEvent(eventData))
)

const teamEventRequestPrice = computed(() => {
  if (!eventData) return false
  if (eventGetters.getNextDateAvailable(eventData)) return false
  const defaultPriceAmount = eventGetters.getDefaultPriceAmount(eventData)

  if (defaultPriceAmount === 0) return false

  return formatSmartPriceAmount(Number(defaultPriceAmount), locale.value)
})

const config = useRuntimeConfig()
const { apiBaseURL } = config.public

onBeforeMount(async () => {
  await nextTick()
  if (isSpecialGiftcardEvent) {
    isBuyBoxReady.value = true
    return
  }

  // Guard: cannot fetch dates without eventData
  if (!eventData) {
    if (import.meta.dev) {
      console.error(
        'EventDetailsSidebar: Cannot fetch calendar dates without eventData'
      )
    }
    isBuyBoxReady.value = true
    return
  }

  try {
    calendarDates.value = await $fetch(
      `/v1/store/events/${eventGetters.getId(eventData)}/calendar`,
      {
        method: 'GET',
        baseURL: apiBaseURL,
      }
    )

    emit('dates-loaded')
  } catch (error) {
    console.error('Failed to fetch calendar dates:', error)
    calendarDates.value = undefined
  } finally {
    isBuyBoxReady.value = true
  }
})
</script>

<style lang="postcss" scoped>
@import '~/assets/css/loading.css';

.preloader {
  @apply rounded-lg;
}
</style>
